// Real availability calendar — shows actual remaining slots for the next 14
// days fetched from /api/availability. Replaces the bare <input type="date">
// with a visual "3 Slots frei" indicator that creates urgency and prevents
// the user from picking a fully-booked day. Falls back gracefully if the API
// is unavailable.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Calendar, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const DAYS = 14;

const WEEKDAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

export default function AvailabilityCalendar({ value, onChange }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/availability?days=${DAYS}`)
      .then((r) => setDays(r.data.availability || []))
      .catch(() => setDays([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-sm text-neutral-500 inline-flex items-center gap-2 py-4" data-testid="availability-loading">
        <Loader2 size={14} className="animate-spin" /> Termine werden geladen…
      </div>
    );
  }

  return (
    <div data-testid="availability-calendar">
      <div className="flex items-center gap-2 mb-3">
        <Calendar size={14} className="text-[#FF5A00]" />
        <span className="text-[11px] font-bold tracking-widest uppercase text-neutral-500">Verfügbare Termine</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
        {days.map((d) => {
          const dt = new Date(d.date + "T00:00:00");
          const isSelected = value === d.date;
          const isFull = d.status === "full";
          const isLow = d.status === "low";
          return (
            <button
              key={d.date}
              type="button"
              disabled={isFull}
              onClick={() => !isFull && onChange?.(d.date)}
              className={`p-2.5 border-2 text-start transition disabled:opacity-40 disabled:cursor-not-allowed ${
                isSelected ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200 hover:border-neutral-400 bg-white"
              }`}
              data-testid={`calendar-day-${d.date}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                {WEEKDAYS_DE[dt.getDay()]}
              </div>
              <div className="text-xl font-black tabular-nums">{dt.getDate().toString().padStart(2, "0")}</div>
              <div className="text-[10px] text-neutral-500">{dt.toLocaleDateString("de-DE", { month: "short" })}</div>
              <div className="mt-1.5 flex items-center gap-1">
                {isFull ? (
                  <span className="text-[10px] font-bold text-red-600 inline-flex items-center gap-0.5">
                    <AlertTriangle size={9} /> Voll
                  </span>
                ) : isLow ? (
                  <span className="text-[10px] font-bold text-amber-600">
                    {d.remaining_slots} frei
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-0.5">
                    <CheckCircle2 size={9} /> Frei
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-neutral-500">
        Live-Verfügbarkeit. Wählen Sie einen freien Tag — bei wenigen verbleibenden Slots empfehlen wir, schnell zu buchen.
      </p>
    </div>
  );
}
